import { fabric } from 'fabric';
import { shortText } from '../print/rules';
import { applyShape } from './picture';

/*
 * ช่องใส่รูป (เทมเพลตสำเร็จรูป, 26 ต.ค. 2569): a box in a template where a picture goes — a Fabric rect with `picSlot`
 * = { hint, shape }. On screen it shows a picture mark and its hint (drawn over the page, never in a file); an empty slot
 * left in an exported file is a pale box. "ใส่รูป" (toolbar) puts a picture in: the picture covers the box (its middle
 * kept, the rest cropped), with the box's round/circle shape, in the box's place in the stack — one undo step.
 * An app from before slots keeps the rect and drops `picSlot`: it becomes a plain pale box (nothing else is lost).
 */
export type SlotShape = 'rect' | 'round' | 'circle';
export interface PicSlot { hint: string; shape: SlotShape }
export const SLOT_FILL = '#ece8f5', SLOT_EDGE = '#b8aed6';
/** a round slot's corners: this part of its shorter side (crop.ts "มุมมน" counts the same way) */
export const SLOT_ROUND = 12;

/** a saved slot made safe to use (anything else = not a slot) */
export function slotOf(o: unknown): PicSlot | null {
  const v = o && typeof o === 'object' ? (o as { picSlot?: unknown }).picSlot : null;
  if (!v || typeof v !== 'object') return null;
  const r = v as Record<string, unknown>;
  const hint = typeof r.hint === 'string' ? shortText(r.hint, 120) : '';
  const shape: SlotShape = r.shape === 'round' || r.shape === 'circle' ? r.shape : 'rect';
  return { hint, shape };
}

/** a new slot w × h at left, top (page units) */
export function makeSlot(left: number, top: number, w: number, h: number, slot: PicSlot): fabric.Rect {
  const r = slot.shape === 'circle' ? Math.min(w, h) / 2 : slot.shape === 'round' ? Math.min(w, h) * SLOT_ROUND / 100 : 0;
  const o = new fabric.Rect({
    left, top, width: w, height: h, rx: r, ry: r, fill: SLOT_FILL, stroke: SLOT_EDGE, strokeWidth: Math.max(2, Math.min(w, h) * 0.006),
    strokeUniform: true, strokeDashArray: [10, 8], name: shortText(`ช่องใส่รูป: ${slot.hint}`, 40)
  });
  (o as fabric.Rect & { picSlot?: PicSlot }).picSlot = { hint: slot.hint, shape: slot.shape };
  return o;
}

/**
 * The part of a W × H picture that covers a box of aspect `aspect` (w ÷ h): the middle, as large as it can be.
 * Returns the crop in picture pixels.
 */
export function coverCrop(W: number, H: number, aspect: number): { x: number; y: number; w: number; h: number } {
  if (!(W > 0 && H > 0 && aspect > 0)) throw new Error('ขนาดรูปหรือช่องไม่ถูกต้อง');
  let w = W, h = W / aspect;
  if (h > H) { h = H; w = H * aspect; }
  return { x: (W - w) / 2, y: (H - h) / 2, w, h };
}

/**
 * A picture made to fill a slot: the same place, size, turn and shape. The slot itself is not touched
 * (the caller swaps them in one undo step).
 */
export function pictureForSlot(el: HTMLImageElement, slot: fabric.Object): fabric.Image {
  const s = slotOf(slot);
  if (!s) throw new Error('ชิ้นนี้ไม่ใช่ช่องใส่รูป');
  const W = el.naturalWidth || el.width, H = el.naturalHeight || el.height;
  if (!(W > 0 && H > 0)) throw new Error('เปิดรูปไม่ได้ (ขนาดรูปเป็น 0)');
  const bw = (slot.width ?? 0) * Math.abs(slot.scaleX ?? 1), bh = (slot.height ?? 0) * Math.abs(slot.scaleY ?? 1);
  const c = coverCrop(W, H, bw / bh);
  const img = new fabric.Image(el, { cropX: c.x, cropY: c.y, width: c.w, height: c.h, scaleX: bw / c.w, scaleY: bh / c.h, angle: slot.angle ?? 0 });
  (img as fabric.Image & { photoShape?: unknown }).photoShape = s.shape === 'rect' ? undefined : { shape: s.shape, round: s.shape === 'round' ? SLOT_ROUND : 0 };
  applyShape(img);
  img.setPositionByOrigin(slot.getCenterPoint(), 'center', 'center');
  img.setCoords();
  return img;
}

/** the picture mark and hint on every empty slot (screen only: drawn after the page, never part of the page) */
export function drawSlotMarks(ctx: CanvasRenderingContext2D, vt: number[], objects: fabric.Object[]) {
  const slots = objects.filter(o => o.visible !== false && slotOf(o));
  if (!slots.length) return;
  ctx.save();
  for (const o of slots) {
    const c = fabric.util.transformPoint(o.getCenterPoint(), vt as unknown as fabric.IPoint[] & number[]);
    const side = Math.min(o.getScaledWidth(), o.getScaledHeight()) * (vt[0] ?? 1);
    if (side < 28) continue;
    const s = Math.min(64, side * 0.22);
    ctx.globalAlpha = 0.9;
    ctx.strokeStyle = '#7d6fb3'; ctx.fillStyle = '#7d6fb3'; ctx.lineWidth = Math.max(1.5, s / 16); ctx.lineJoin = 'round';
    // a small picture: frame, hill, sun
    ctx.strokeRect(c.x - s / 2, c.y - s * 0.62, s, s * 0.75);
    ctx.beginPath(); ctx.moveTo(c.x - s / 2, c.y + 0.1 * s); ctx.lineTo(c.x - s * 0.12, c.y - s * 0.28); ctx.lineTo(c.x + s * 0.14, c.y - 0.02 * s);
    ctx.lineTo(c.x + s * 0.28, c.y - s * 0.16); ctx.lineTo(c.x + s / 2, c.y + 0.1 * s); ctx.stroke();
    ctx.beginPath(); ctx.arc(c.x + s * 0.24, c.y - s * 0.4, s * 0.08, 0, Math.PI * 2); ctx.fill();
    if (side > 120) {
      const hint = slotOf(o)!.hint;
      ctx.font = `600 ${Math.round(Math.max(11, Math.min(18, side / 14)))}px Sarabun, sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'top';
      ctx.fillText('ใส่รูป', c.x, c.y + s * 0.3);
      if (hint && side > 200) {
        ctx.globalAlpha = 0.7;
        const max = Math.max(60, side * 0.85);
        // shortened without splitting a Thai vowel or tone mark from its letter (BUG-6)
        let n = [...hint].length, t = hint;
        while (n > 4 && ctx.measureText(t).width > max) t = shortText(hint, n -= 2);
        ctx.fillText(t, c.x, c.y + s * 0.3 + Math.max(14, Math.min(24, side / 11)));
      }
    }
  }
  ctx.restore();
}
